/**
 * PrintToPdfButton — "Save as PDF" affordance that triggers window.print().
 *
 * The browser's print dialog has a "Save as PDF" destination as the
 * default on macOS / Windows / Linux Chrome. The print stylesheet in
 * styles.css hides the on-screen chrome (nav, action buttons, diag
 * panel, etc.) and applies page-break rules so the resulting PDF is
 * clean and bound to the v10 grammar.
 *
 * --- FEATURE GATE HOOK (Russ 2026-05-09 product call) -----------------
 * When PeoplePrints productizes for public consumption, free-trial
 * accounts should NOT be able to export PDFs (prevents the
 * generate-everything-then-cancel abuse pattern). The `disabledForTier`
 * prop is the hook for that future gate. Default: undefined (always
 * enabled, V1 behavior). When V1.y ships paid tiers, callers set
 * `disabledForTier="trial"` and the button renders a disabled state
 * with a "Upgrade to enable PDF export" tooltip.
 *
 * The hook lives at the component layer (not at the Blueprint render
 * page) so flipping it across all six surfaces is one line in this
 * component, not six.
 */

import { useState } from "react";

export default function PrintToPdfButton({
  label = "Save as PDF",
  // Feature-gate hook (Russ 2026-05-09): when true, the button renders
  // disabled with an upgrade-prompt tooltip. Wired into nothing in V1
  // — every caller passes false (or omits). Wired in V1.y when paid
  // tiers are introduced.
  disabled = false,
  disabledReason = "PDF export is available on paid plans. Upgrade to enable.",
  className,
  style,
}) {
  const [printing, setPrinting] = useState(false);

  const onClick = () => {
    if (disabled) return;
    setPrinting(true);
    // window.print() is synchronous + blocks until the print dialog
    // closes. Set a flag for visual feedback, clear after a short
    // delay (the dialog may have closed by then).
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrinting(false), 600);
    }, 50);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledReason : "Open the print dialog. Choose 'Save as PDF' as the destination."}
      data-component="PrintToPdfButton"
      data-testid="print-to-pdf-button"
      data-disabled-for-tier={disabled || undefined}
      className={className}
      style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        padding: "10px 18px",
        background: disabled ? "var(--ppr-color-background-muted)" : "var(--ppr-color-accent-navy)",
        color: disabled ? "var(--ppr-color-text-muted)" : "var(--ppr-color-text-inverse)",
        border: `1px solid ${disabled ? "var(--ppr-color-border-default)" : "var(--ppr-color-accent-navy)"}`,
        borderRadius: "var(--ppr-radius-sm)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: printing ? 0.6 : 1,
        transition: "opacity 120ms ease-out",
        ...style,
      }}
    >
      {printing ? "Opening print dialog…" : label}
    </button>
  );
}
